// frontend/src/components/Header.js
// This React component renders the app header with the system title and a user menu, providing quick access to account settings and a logout option with proper dropdown handling.
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../features/auth/authSlice';
import { User } from 'lucide-react';

// Header with user dropdown for account settings & logout
export default function Header() {
  const [open, setOpen] = useState(false);
  const headerRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const menuRootRef = useRef(null);

  // Expose header height as a CSS variable (updates on resize)
  useEffect(() => {
    const setVar = () => {
      if (headerRef.current) {
        document.documentElement.style.setProperty(
          '--app-header-h',
          `${headerRef.current.offsetHeight}px`
        );
      }
    };
    setVar();
    window.addEventListener('resize', setVar);
    return () => window.removeEventListener('resize', setVar);
  }, []);


  // Handle click outside to close dropdown
  useEffect(() => {
    function handleClickOutside(event) {
      if (menuRootRef.current && !menuRootRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Logout flow: dispatch then redirect
  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/login');
    } catch {
      console.error('Logout failed');
    }
  };

  return (
    <header ref={headerRef} className="relative z-[60] bg-primary text-white shadow-md">
      <div className="container mx-auto px-4 py-3 flex items-center justify-between">
        {/* Title */}
        <h1 className="text-xl font-semibold">Driver Management System</h1>

        {/* User dropdown toggle */}
        <div ref={menuRootRef} className="relative">
          <button
            onClick={() => setOpen(o => !o)}
            className="p-2 rounded-full hover:bg-white/20 focus:outline-none"
            aria-label="User menu"
          >
            <User size={20} />
          </button>

          {/* Dropdown menu */}
          {open && (
            <div
              className="absolute right-0 mt-2 w-48 bg-white text-black rounded shadow-lg overflow-hidden z-[70]"
            >
              <button
                onClick={() => { setOpen(false); navigate('/profile'); }}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Account Settings
              </button>
              <button
                onClick={handleLogout}
                className="block w-full text-left px-4 py-2 hover:bg-gray-100"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
);}
